import React, { createContext, useContext, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import Cookies from 'universal-cookie'

import { destroy, get, post } from './Http'
import { capitalize } from '../helpers'

export interface UserData {
  full_name?: string
  title?: string
  entity_name?: string
  active_roles?: string[]
  allowed_roles?: string[]
  allowed_applications?: string[]
  metadata?: Record<string, unknown>
  [key: string]: unknown
}

interface UserContextValue {
  user: UserData
  setUser: React.Dispatch<React.SetStateAction<UserData>>
}

const UserContext = createContext<UserContextValue | undefined>(undefined)

const getUserData = (): UserData => {
  let userDataRaw = localStorage.getItem('userData')
  return userDataRaw == null ? {} : JSON.parse(userDataRaw)
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserData>(getUserData)
  return React.createElement(UserContext.Provider, { value: { user, setUser } }, children)
}

const token = (sessionCookieName: string): string => {
  const cookies = new Cookies()
  return cookies.get(sessionCookieName)
}

const activeRole = (user: UserData, scope: string) => (user.active_roles || []).find((role) => role.startsWith(scope))

const nextRole = (user: UserData, scope: string) => {
  const activeRole = (user.active_roles || []).find((role) => role.startsWith(scope))
  const roles = (user.allowed_roles || []).filter((role) => role.startsWith(scope))
  if (!activeRole) return roles.length > 1 ? roles[0] : undefined
  const rolesRotated = [...roles, ...roles]
  const nextRoleIndex = rolesRotated.indexOf(activeRole) + 1
  return roles.length > 1 ? rolesRotated[nextRoleIndex] : undefined
}

export function useUserService(authServiceUrl: string, sessionCookieName: string, scope: string) {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUserService must be used within a UserProvider')
  }
  const { user, setUser } = context
  const lazyToken = () => token(sessionCookieName)

  const updateUser = (data: UserData) => {
    // A sign-in that stopped at the second factor is not a signed-in user. It
    // carries no profile, only a short-lived token for the next step, and
    // storing it would leave the apps reading a half-empty userData and the
    // token itself sitting in localStorage.
    if (data?.mfa_required) return data
    // Http resolves false for a 2xx whose body is not JSON (a flaky network, a captive
    // portal). Keep the signed-in user rather than store an empty one, which hides
    // every role-gated menu item until the next reload.
    if (!data || typeof data !== 'object') return data

    // Never persist the auth token to localStorage: it is XSS-exfiltratable and
    // survives logout. The token lives only in the session cookie (read via
    // token()); cache profile fields only.
    const profile: UserData = { ...data }
    delete profile.token
    localStorage.setItem('userData', JSON.stringify(profile))
    setUser(profile)
    return profile
  }

  const loginMutation = useMutation({
    mutationFn: ({ email, password, validationToken }: { email: string; password: string; validationToken: string }) =>
      post({
        url: `${authServiceUrl}/api/v1/users/login`,
        payload: { user: { email, password }, validation_token: validationToken },
        token: lazyToken,
      }),
    onSuccess: updateUser,
    throwOnError: false,
  })

  // The second half of signing in. Here beside login rather than in IAM alone
  // so that what a completed sign-in does to stored state is decided in one
  // place: all three of these finish with the same session data and the same
  // updateUser, whichever step produced it.
  //
  // The MFA token is passed in the payload, not the Authorization header. It is
  // not a session, and putting it where a session goes is how it ends up being
  // treated as one.
  const verifyMfaMutation = useMutation({
    mutationFn: ({ mfaToken, code }: { mfaToken: string; code: string }) =>
      post({ url: `${authServiceUrl}/api/v1/mfa/challenge`, payload: { mfa_token: mfaToken, otp_code: code } }),
    onSuccess: updateUser,
    throwOnError: false,
  })

  const startMfaEnrolmentMutation = useMutation({
    mutationFn: ({ mfaToken }: { mfaToken: string }) =>
      post({ url: `${authServiceUrl}/api/v1/mfa/enrolment`, payload: { mfa_token: mfaToken } }),
    throwOnError: false,
  })

  const confirmMfaEnrolmentMutation = useMutation({
    mutationFn: ({ mfaToken, code }: { mfaToken: string; code: string }) =>
      post({ url: `${authServiceUrl}/api/v1/mfa/enrolment/confirm`, payload: { mfa_token: mfaToken, otp_code: code } }),
    onSuccess: (data: { session: UserData }) => updateUser(data.session),
    throwOnError: false,
  })

  const syncMutation = useMutation({
    mutationFn: () =>
      get({ url: `${authServiceUrl}/api/v1/users/sync`, token: lazyToken }),
    onSuccess: updateUser,
    throwOnError: false,
  })

  const switchRoleMutation = useMutation({
    mutationFn: (role: string) =>
      post({
        url: `${authServiceUrl}/api/v1/users/switch_role`,
        payload: { role },
        token: lazyToken,
      }),
    onSuccess: updateUser,
  })

  const onboardingProfileMutation = useMutation({
    mutationFn: ({ user_role, user_title }: { user_role: string; user_title?: string }) =>
      post({
        url: `${authServiceUrl}/api/v1/users/onboarding_profile`,
        payload: { user_role, user_title },
        token: lazyToken,
      }),
    onSuccess: updateUser,
  })

  return {
    user,
    token: () => token(sessionCookieName),
    activeRole: () => activeRole(user, scope),
    activeRoleName: () => {
      const role = activeRole(user, scope)
      return role ? capitalize(role.split(':')[1]) : undefined
    },
    nextRole: () => nextRole(user, scope),
    nextRoleName: () => {
      const role = nextRole(user, scope)
      return role ? capitalize(role.split(':')[1]) : undefined
    },
    login: (params: { email: string; password: string; validationToken: string }) =>
      loginMutation.mutateAsync(params),
    isLoggingIn: loginMutation.isPending,
    verifyMfa: (params: { mfaToken: string; code: string }) => verifyMfaMutation.mutateAsync(params),
    isVerifyingMfa: verifyMfaMutation.isPending,
    startMfaEnrolment: (params: { mfaToken: string }) => startMfaEnrolmentMutation.mutateAsync(params),
    confirmMfaEnrolment: (params: { mfaToken: string; code: string }) =>
      confirmMfaEnrolmentMutation.mutateAsync(params),
    isConfirmingMfaEnrolment: confirmMfaEnrolmentMutation.isPending,
    sync: () => syncMutation.mutateAsync(),
    isSyncing: syncMutation.isPending,
    switchRole: (role: string) => switchRoleMutation.mutateAsync(role),
    updateOnboardingProfile: (params: { user_role: string; user_title?: string }) =>
      onboardingProfileMutation.mutateAsync(params),
    resetUser: () => {
      localStorage.removeItem('userData')
      setUser({})
    },
    _logout: async () => { // NOTE: only meant to be used in IAM service
      localStorage.removeItem('userData')
      setUser({})
      await destroy({ url: `${authServiceUrl}/api/v1/users/logout` })
    },
    isUserInRole: (role: string) => {
      return !role || (user.active_roles || []).includes(role)
    },
    register: async (payload: Record<string, unknown>) => {
      return post({ url: `${authServiceUrl}/api/v1/users`, payload, token: lazyToken })
    },
  }
}
